import { useMemo } from "react";

import { VERSIONS } from "~/providers/constants";
import { isConsultationCompleted } from "~/providers/utils";
import { getConsultationSummaryV3Data } from "~/providers/utils/consultationRequests/patientPreview/getConsultationSummaryV3Data";
import { getConsultationSummaryV4Data } from "~/providers/utils/consultationRequests/patientPreview/getConsultationSummaryV4Data";
import { ConsultationExport } from "~/providers/utils/types/consultation";
import { useGetPatient } from "../useGetPatient";
import { useGetConsultationReport } from "./useGetConsultationReport";
import { useGetPatientConsultationSummary } from "./useGetPatientConsultationSummary";

export const useGetConsultationExport = ({
  patientId,
}: {
  patientId: string;
}): { isLoading: boolean; data: ConsultationExport } => {
  const patientQuery = useGetPatient(patientId);

  const consultation = patientQuery.data?.consultation;

  const reportQuery = useGetConsultationReport(consultation?.id, {
    enabled: isConsultationCompleted(consultation?.status),
    staleTime: 5000,
  });

  const summaryQuery = useGetPatientConsultationSummary(
    consultation?.id,
    Boolean(consultation?.has_screening_summary),
    Boolean(consultation?.has_intake_summary),
  );

  const summary = useMemo(() => {
    if (!summaryQuery.screeningSummaryData || !summaryQuery.intakeSummaryData) {
      return undefined;
    }

    if (
      summaryQuery.screeningSummaryData.version === VERSIONS.v4 &&
      summaryQuery.intakeSummaryData.version === VERSIONS.v4
    ) {
      return getConsultationSummaryV4Data({
        screeningSummary: summaryQuery.screeningSummaryData,
        intakeSummary: summaryQuery.intakeSummaryData,
      });
    }

    if (
      summaryQuery.screeningSummaryData.version === VERSIONS.v3 &&
      summaryQuery.intakeSummaryData.version === VERSIONS.v3
    ) {
      return getConsultationSummaryV3Data({
        screeningSummary: summaryQuery.screeningSummaryData,
        intakeSummary: summaryQuery.intakeSummaryData,
      });
    }

    return undefined;
  }, [summaryQuery.screeningSummaryData, summaryQuery.intakeSummaryData]);

  return {
    data: {
      patient: patientQuery.data!,
      report: reportQuery.data!,
      summary,
    },
    isLoading:
      patientQuery.isLoading || reportQuery.isLoading || summaryQuery.isLoading,
  };
};
